import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Chip,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import RemoveRedEyeRoundedIcon from '@mui/icons-material/RemoveRedEyeRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import { useTranslation } from 'react-i18next';

import api from '../../api/axiosConfig';
import DocumentMediaDialog from '../../components/DocumentMediaDialog';
import {
  getInformeMediaUrl,
  getInformeThumbUrl,
  isImageMedia,
  isPdfMedia,
  isHtmlMedia,
  asMediaDoc,
} from '../../utils/informeMedia';
import { useInstatDesign } from './theme/instatDesign';
import { Card, Pill } from './ui';
import { isExtensionInstalled } from './instatExtension';
import { openHudlPlayerForInforme } from './instatInforme';
import ClipShelf from './ClipShelf';
import TelestrationEditor from './Telestration/TelestrationEditor';

function formatDate(isoLike) {
  if (!isoLike) return '';
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return String(isoLike);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

// Convert detalles HTML to safe-ish display: it's stored as HTML so we
// render with dangerouslySetInnerHTML — same approach used in InformeTab.js.
// Strip trailing <br/> noise that's common in the data.
function cleanDetalles(html) {
  if (!html) return '';
  return String(html).replace(/(<br\s*\/?>\s*)+$/i, '');
}

export default function InformesTab({
  localPlayerId,
  instatPlayerId,
  playerName,
  onCountChange,
}) {
  const d = useInstatDesign();
  const { t } = useTranslation();

  const [informes, setInformes] = useState(null);
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playingClip, setPlayingClip] = useState(null);
  const [extInstalled, setExtInstalled] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!localPlayerId) {
      setInformes([]);
      setClips([]);
      return;
    }
    setLoading(true);
    try {
      const [{ data: informesData }, { data: clipsData }] = await Promise.all([
        api.get('/api/informe', { params: { playerId: localPlayerId } }),
        api.get('/api/instat/clips', { params: { playerId: localPlayerId } }),
      ]);
      setInformes(Array.isArray(informesData) ? informesData : []);
      setClips(clipsData?.clips || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[InformesTab] fetch failed:', e?.message);
      setInformes([]);
      setClips([]);
    } finally {
      setLoading(false);
    }
  }, [localPlayerId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    // Recheck on mount — the extension marker is set by app-bridge.js at
    // document_start, so by the time this effect runs it's reliable.
    setExtInstalled(isExtensionInstalled());
  }, []);

  useEffect(() => {
    if (onCountChange) onCountChange(informes ? informes.length : null);
  }, [informes, onCountChange]);

  const clipsByInformeId = useMemo(() => {
    const acc = {};
    clips.forEach((c) => {
      if (!c.informe_id) return;
      if (!acc[c.informe_id]) acc[c.informe_id] = [];
      acc[c.informe_id].push(c);
    });
    return acc;
  }, [clips]);

  const onAddClips = (informe) => {
    if (!instatPlayerId) {
      alert(t('instat.informes.noInstatId'));
      return;
    }
    openHudlPlayerForInforme({
      instatPlayerId,
      localPlayerId,
      informeId: informe.id,
      informeTitle: `${informe.evento || ''} · ${formatDate(informe.fecha)}`.trim(),
    });
  };

  if (!localPlayerId) {
    return (
      <Card>
        <Typography sx={{ ...d.typo.caption }}>{t('instat.informes.localPlayerRequired')}</Typography>
      </Card>
    );
  }

  if (loading && !informes) {
    return (
      <Card>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CircularProgress size={16} sx={{ color: d.colors.textSecondary }} />
          <Typography sx={{ ...d.typo.caption }}>{t('common.loading')}</Typography>
        </Stack>
      </Card>
    );
  }

  if (informes && informes.length === 0) {
    return (
      <Card>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ ...d.typo.title }}>{t('instat.informes.title')}</Typography>
          <Tooltip title={t('instat.clips.refresh')}>
            <span>
              <IconButton size="small" onClick={fetchAll} disabled={loading}>
                {loading
                  ? <CircularProgress size={16} sx={{ color: d.colors.textSecondary }} />
                  : <RefreshRoundedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Typography sx={{ ...d.typo.caption }}>{t('instat.informes.empty')}</Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, px: 0.5 }}>
        <Typography sx={{ ...d.typo.title, fontSize: 16 }}>
          {t('instat.informes.title')} ({informes?.length || 0})
        </Typography>
        <Tooltip title={t('instat.clips.refresh')}>
          <span>
            <IconButton size="small" onClick={fetchAll} disabled={loading}>
              {loading
                ? <CircularProgress size={14} sx={{ color: d.colors.textSecondary }} />
                : <RefreshRoundedIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack spacing={2}>
        {(informes || []).map((informe) => (
          <InformeCard
            key={informe.id}
            informe={informe}
            clips={clipsByInformeId[informe.id] || []}
            playerName={playerName}
            instatPlayerId={instatPlayerId}
            extInstalled={extInstalled}
            onAddClips={() => onAddClips(informe)}
            onPlayClip={setPlayingClip}
            t={t}
            d={d}
          />
        ))}
      </Stack>

      <TelestrationEditor
        clip={playingClip}
        open={!!playingClip}
        onClose={() => setPlayingClip(null)}
        playlist={clips}
        onNavigate={setPlayingClip}
        onTagsChange={(clipId, tags) => {
          setClips((prev) => prev.map((c) => (c.id === clipId ? { ...c, tags } : c)));
          setPlayingClip((prev) => (prev && prev.id === clipId ? { ...prev, tags } : prev));
        }}
      />
    </Box>
  );
}

function InformeCard({
  informe,
  clips,
  instatPlayerId,
  extInstalled,
  onAddClips,
  onPlayClip,
  t,
  d,
}) {
  // Pièce jointe HTML : aperçu in-app dans une iframe sandbox (même dialog que les documents)
  const [htmlOpen, setHtmlOpen] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);

  const visu = informe.visu === 'video'
    ? { icon: <VideocamRoundedIcon sx={{ fontSize: 14 }} />, label: 'Video' }
    : { icon: <RemoveRedEyeRoundedIcon sx={{ fontSize: 14 }} />, label: 'Live' };

  const fitChips = [
    { key: 'fit_estilo',   label: 'Estilo' },
    { key: 'fit_fisico',   label: 'Físico' },
    { key: 'fit_actitud',  label: 'Actitud' },
    { key: 'fit_mejora',   label: 'Mejora' },
    { key: 'fit_contexto', label: 'Contexto' },
  ].filter((f) => informe[f.key] === 1 || informe[f.key] === 0);

  return (
    <Card padding={0} sx={{ overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${d.colors.divider}` }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography sx={{ ...d.typo.title, fontSize: 15 }} noWrap>
                {informe.evento || t('instat.informes.untitled')}
              </Typography>
              <Pill tone="outline" size="sm">{formatDate(informe.fecha)}</Pill>
              <Pill tone="outline" size="sm">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {visu.icon}
                  <span>{visu.label}</span>
                </Stack>
              </Pill>
              {informe.equipo && (
                <Pill tone="outline" size="sm">{informe.equipo}</Pill>
              )}
              {informe.nivel && (
                <Pill tone="accent" size="sm">{informe.nivel}</Pill>
              )}
              {informe.projection && (
                <Pill tone="accent" size="sm">{informe.projection}</Pill>
              )}
            </Stack>
            <Typography sx={{ ...d.typo.caption, mt: 0.5 }}>
              {informe.usuario_nombre || `User ${informe.usuario_id}`}
            </Typography>
          </Box>

          <Tooltip
            title={
              !extInstalled
                ? t('instat.informes.extensionRequired')
                : !instatPlayerId
                  ? t('instat.informes.noInstatId')
                  : t('instat.informes.addClipsTooltip')
            }
          >
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={onAddClips}
                disabled={!extInstalled || !instatPlayerId}
                sx={{
                  borderRadius: `${d.radius.pill}px`,
                  textTransform: 'none',
                  fontWeight: 600,
                  background: d.accentGradient,
                  boxShadow: d.shadows.card,
                  '&:hover': { background: d.accentGradient, boxShadow: d.shadows.cardHover },
                }}
              >
                {t('instat.informes.addClips')}
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {fitChips.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
            {fitChips.map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: informe[f.key] === 1 ? '#ECFDF5' : '#FEF2F2',
                  color:   informe[f.key] === 1 ? '#065F46' : '#B91C1C',
                  border: 'none',
                }}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Body : detalles + media (if any) */}
      {(informe.detalles || informe.media_filename) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: informe.media_filename ? '1fr 220px' : '1fr' },
            gap: 2,
            px: 2,
            py: 1.5,
          }}
        >
          {informe.detalles && (
            <Box
              sx={{
                ...d.typo.body,
                fontSize: 13.5,
                lineHeight: 1.55,
                color: d.colors.text,
                '& p': { m: 0, mb: 1 },
                '& ul': { my: 0, pl: 2.5 },
              }}
              dangerouslySetInnerHTML={{ __html: cleanDetalles(informe.detalles) }}
            />
          )}
          {informe.media_filename && (
            <Box>
              {isImageMedia(informe.media_filename) ? (
                <Box
                  component="img"
                  src={getInformeMediaUrl(informe.media_filename)}
                  alt={informe.media_filename}
                  sx={{
                    width: '100%',
                    borderRadius: `${d.radius.md}px`,
                    border: `1px solid ${d.colors.divider}`,
                    display: 'block',
                  }}
                />
              ) : isPdfMedia(informe.media_filename) ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfRoundedIcon />}
                  onClick={() => window.open(getInformeMediaUrl(informe.media_filename), '_blank')}
                  sx={{ textTransform: 'none', borderRadius: `${d.radius.md}px` }}
                >
                  {informe.media_filename}
                </Button>
              ) : isHtmlMedia(informe.media_filename) ? (
                thumbFailed ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CodeRoundedIcon />}
                    onClick={() => setHtmlOpen(true)}
                    sx={{ textTransform: 'none', borderRadius: `${d.radius.md}px` }}
                  >
                    {t('playerCardCreator.informeTab.viewHtml')}
                  </Button>
                ) : (
                  <Box
                    component="img"
                    src={getInformeThumbUrl(informe.id)}
                    alt={informe.media_filename}
                    onClick={() => setHtmlOpen(true)}
                    onError={() => setThumbFailed(true)}
                    sx={{
                      width: '100%',
                      borderRadius: `${d.radius.md}px`,
                      border: `1px solid ${d.colors.divider}`,
                      display: 'block',
                      cursor: 'pointer',
                    }}
                  />
                )
              ) : (
                <Chip
                  label={informe.media_filename}
                  onClick={() => window.open(getInformeMediaUrl(informe.media_filename), '_blank')}
                  size="small"
                />
              )}
              <DocumentMediaDialog
                open={htmlOpen}
                doc={htmlOpen ? asMediaDoc(informe) : null}
                fileUrl={getInformeMediaUrl(informe.media_filename)}
                onClose={() => setHtmlOpen(false)}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Footer : linked clips */}
      <Box sx={{ borderTop: `1px solid ${d.colors.divider}`, bgcolor: d.colors.surfaceAlt }}>
        <Stack direction="row" alignItems="center" sx={{ px: 2, pt: 1.25, pb: clips.length ? 0.5 : 1.25 }}>
          <Typography sx={{ ...d.typo.micro, color: d.colors.textSecondary, fontWeight: 600 }}>
            {t('instat.informes.linkedClips')} · {clips.length}
          </Typography>
        </Stack>
        {clips.length > 0 ? (
          <ClipShelf
            clips={clips}
            onPlay={onPlayClip}
            t={t}
            cardWidth={{ xs: 200, sm: 220 }}
            sx={{ mb: 0, px: 1.5, pb: 1.25 }}
          />
        ) : (
          <Typography sx={{ ...d.typo.caption, px: 2, pb: 1.5 }}>
            {t('instat.informes.noClipsYet')}
          </Typography>
        )}
      </Box>
    </Card>
  );
}
